import * as React from 'react'
import {
  IonButton,
  IonCol,
  IonContent,
  IonHeader,
  IonIcon,
  IonImg,
  IonInput,
  IonItem,
  IonLabel,
  IonLoading,
  IonModal,
  IonPage,
  IonRouterLink,
  IonRow,
  IonSelect,
  IonSelectOption,
  IonText,
  useIonAlert,
} from '@ionic/react'
import { Controller, useForm } from 'react-hook-form'
import { format } from 'date-fns'
import { close } from 'ionicons/icons'
import { Redirect } from 'react-router-dom'

import * as commonStrings from '../../utils/common-strings'
import { useSmsMaxCharacterCount } from '../../utils/forms'
import {
  selectors,
  useBroadcast,
  useBroadcastActions,
} from '../../marketing/broadcasts/broadcast-store'
import { useUser } from '../../contexts/authContext'
import { useToast } from '../../contexts/toastContext'
import { useSmsCredits } from '../../hooks/account/queries'
import { usePullClientList } from '../../hooks/clients/queries'
import { useTemplates } from '../../hooks/templates/queries'
import { useReplacePlaceholders } from '../../hooks/use-replace-placeholders'
import { useShopWithLinks } from '../../hooks/shopwith/use-shopwith-links'
import PullClientsModal from '../../components/client/pull-clients-modal'
import Header from '../../components/header'
import { FilterButton } from '../../components/ui/buttons/filter-button'
import BroadcastFab from '../../components/fab/broadcast-fab'
import { Checkbox } from '../../components/ui/form/checkbox'
import {
  MergeFieldButton,
  MergeFieldButtons,
} from '../../components/ui/form/merge-field-buttons'
import { TextArea } from '../../components/ui/form/textarea'
import { useIsStore } from '../../auth/utils'
import { Card, CardContent } from '../../marketing/broadcasts/components/ui'
import { useQueueBroadcast } from '../../marketing/broadcasts/mutations'

import type { SelectChangeEventDetail } from '@ionic/core'
import type { Template } from '../../hooks/templates/queries'
import type { PhotoData } from '../../hooks/use-photo'
import type { QueueBroadcastInput } from '../../marketing/broadcasts/mutations'
import type { FiltersFromForm } from '../../utils/compose-search-filters'

type FormState = {
  broadcastTitle: string
  smsMessage: string
  emailSubject: string
  emailMessage: string
  includeSms: boolean
  includeEmail: boolean
  emailOnly: boolean
  includeRecentContacts: boolean
  collectionIds: Array<string>
  lookIds: Array<string>
  productIds: Array<string>
  photo: PhotoData | string | null
}

// the excluded values will come in through broadcast init
const defaultFormValues: Omit<
  FormState,
  'includeSms' | 'includeEmail' | 'includeRecentContacts' | 'emailOnly'
> = {
  broadcastTitle: '',
  smsMessage: '',
  emailSubject: '',
  emailMessage: '',
  collectionIds: [],
  lookIds: [],
  productIds: [],
  photo: null,
}

function CreateBroadcast() {
  const smsMessageRef = React.useRef<HTMLIonTextareaElement>(null)
  const emailMessageRef = React.useRef<HTMLIonTextareaElement>(null)

  const [template, setTemplate] = React.useState<Template>()
  const [creditsUsed, setCreditsUsed] = React.useState(0)
  const [isFilterOpen, setIsFilterOpen] = React.useState(false)
  const [filters, setFilters] = React.useState<FiltersFromForm>({})

  const user = useUser()

  const setToast = useToast()
  const { isUno } = useIsStore()
  const [presentAlert] = useIonAlert()

  const broadcastState = useBroadcast(selectors.broadcastState)
  const clientList = useBroadcast(selectors.clientList)
  const smsClientList = useBroadcast(selectors.filteredSmsList)
  const emailClientList = useBroadcast(selectors.filteredEmailList)
  const includeSms = useBroadcast.use.includeSms()
  const includeEmail = useBroadcast.use.includeEmail()
  const emailOnly = useBroadcast.use.emailOnly()
  const includeRecentContacts = useBroadcast.use.includeRecentContacts()
  const broadcastTemplate = useBroadcast.use.template()
  const callListId = useBroadcast.use.callListId()
  const callListDynamicId = useBroadcast.use.callListDynamicId()

  const {
    reset: resetBroadcast,
    updateClientList,
    setFilter: setBroadcastFilter,
  } = useBroadcastActions()

  const { generateShopWithLinkPreview } = useShopWithLinks()
  const { getSmsMaxCharacterCount } = useSmsMaxCharacterCount()
  const smsCredits = useSmsCredits()

  const templatesQuery = useTemplates()
  const pullClientListQuery = usePullClientList(filters, {
    enabled: !broadcastState.prefiltered,
  })

  const queueBroadcastMutation = useQueueBroadcast()

  const { replacePlaceholders } = useReplacePlaceholders()

  const { control, setValue, watch, resetField, handleSubmit } =
    useForm<FormState>({
      defaultValues: {
        ...defaultFormValues,
        includeSms,
        includeEmail,
        emailOnly,
        includeRecentContacts,
        smsMessage: broadcastTemplate?.smsTemplate?.body ?? '',
        emailSubject: broadcastTemplate?.emailTemplate?.subject ?? '',
        emailMessage: broadcastTemplate?.emailTemplate?.body ?? '',
        photo: broadcastTemplate?.smsTemplate?.attachments
          ? broadcastTemplate?.smsTemplate?.attachments[0]
          : null,
        productIds: broadcastTemplate?.products?.map((p) => p.objectId) ?? [],
        lookIds: broadcastTemplate?.looks?.map((l) => l.objectId) ?? [],
        collectionIds:
          broadcastTemplate?.collections?.map((c) => c.objectId) ?? [],
      },
    })

  const [
    watchSmsMessage,
    watchProductIds,
    watchLookIds,
    watchCollectionIds,
    watchPhoto,
  ] = watch(['smsMessage', 'productIds', 'lookIds', 'collectionIds', 'photo'])

  // udapted disable logic to include timeout errors (see useQueueBroadcast for explanation)
  const isBroadcastDisabled =
    queueBroadcastMutation.isLoading ||
    queueBroadcastMutation.isSuccess ||
    (queueBroadcastMutation.isError &&
      queueBroadcastMutation.error instanceof Error &&
      queueBroadcastMutation.error.name !== 'TimeoutError')

  const maxCharacterCount = getSmsMaxCharacterCount(watchSmsMessage)

  const isEditingDisabled = isUno() && Boolean(callListId)

  React.useEffect(() => {
    if (!pullClientListQuery.data || broadcastState.prefiltered) return

    if (broadcastState.initialized) {
      updateClientList(pullClientListQuery.data)
    }
  }, [
    broadcastState.initialized,
    broadcastState.prefiltered,
    pullClientListQuery.data,
    updateClientList,
  ])

  React.useEffect(() => {
    return () => {
      resetBroadcast()
    }
  }, [resetBroadcast])

  React.useEffect(() => {
    if (watchPhoto) {
      setCreditsUsed(smsClientList.length * 3)
    } else {
      setCreditsUsed(smsClientList.length)
    }
  }, [smsClientList.length, watchPhoto])

  function handleTemplateSelect(
    event: CustomEvent<SelectChangeEventDetail<string>>
  ) {
    const selectedTemplate = templatesQuery.data?.find(
      (template) => template.objectId === event.detail.value
    )

    if (!selectedTemplate) {
      return
    }

    setTemplate(selectedTemplate)
    setValue(
      'smsMessage',
      replacePlaceholders(selectedTemplate.smsTemplate?.body ?? '')
    )
    setValue(
      'emailSubject',
      replacePlaceholders(selectedTemplate.emailTemplate?.subject ?? '')
    )
    setValue(
      'emailMessage',
      replacePlaceholders(selectedTemplate.emailTemplate?.body ?? '')
    )

    setValue(
      'productIds',
      selectedTemplate.products?.map((p) => p.objectId) ?? []
    )
    setValue('lookIds', selectedTemplate.looks?.map((l) => l.objectId) ?? [])
    setValue(
      'collectionIds',
      selectedTemplate.collections?.map((c) => c.objectId) ?? []
    )
    // TODO: HACK!!! SHOULD EVENTUALLY MOVE TO SAME STRUCTURE AS TEMPLATES WITH ATTACHMENTS ON EACH "TEMPLATE" TYPE
    setValue(
      'photo',
      selectedTemplate.smsTemplate?.attachments
        ? selectedTemplate.smsTemplate.attachments[0]
        : null
    )
  }

  function handleShopWithSelected(
    type: 'productIds' | 'lookIds' | 'collectionIds',
    value: Array<string>
  ) {
    switch (type) {
      case 'productIds':
        setValue('productIds', value)
        resetField('lookIds')
        resetField('collectionIds')
        break
      case 'lookIds':
        setValue('lookIds', value)
        resetField('productIds')
        resetField('collectionIds')
        break
      case 'collectionIds':
        setValue('collectionIds', value)
        resetField('productIds')
        resetField('lookIds')
        break
      default:
        throw new Error('unhandled case')
    }
  }

  function handleSelectPhoto(photo: PhotoData) {
    setValue('photo', photo)
  }

  function handleTakePhoto(photo: PhotoData) {
    setValue('photo', photo)
  }

  function validateBroadcast(data: FormState) {
    if (!smsCredits.data) {
      setToast({ message: 'There was an issue loading your credits.' })
      return false
    }
    if (creditsUsed > smsCredits.data?.smsCreditsRemaining) {
      setToast({
        message:
          'You do not have enough credits to send this broadcast. Please contact your OneShop representative to purchase more.',
      })
      return false
    }

    if (clientList.length === 0) {
      setToast({ message: 'No clients found' })
      return false
    }

    if (smsClientList.length > 0 && data.smsMessage.length === 0) {
      setToast({ message: 'Missing text message' })
      return false
    }

    if (
      emailClientList.length > 0 &&
      (!data.emailSubject || !data.emailMessage)
    ) {
      setToast({ message: 'Missing email message or subject' })
      return false
    }

    if (
      smsClientList.length > 0 &&
      data.smsMessage.length > maxCharacterCount
    ) {
      setToast({
        message: 'Text message is longer than the max allowed',
      })
      return false
    }

    return true
  }

  async function handleSendBroadcast(formData: FormState) {
    const isValidBroadcast = validateBroadcast(formData)
    if (!isValidBroadcast) return

    presentAlert({
      header: 'Alert',
      message: `You are about to send this broadcast to ${
        smsClientList.length + emailClientList.length
      } customers. This cannot be undone. Are you sure?`,
      buttons: [
        'Cancel',
        {
          text: 'Send',
          handler: async () => {
            const attachments = !watchPhoto
              ? undefined
              : typeof watchPhoto === 'string'
              ? [watchPhoto]
              : [{ content: watchPhoto.base64String, name: watchPhoto.name }]

            const broadcastData: QueueBroadcastInput = {
              type: 'associate',
              title:
                formData.broadcastTitle ||
                `Broadcast ${format(new Date(), 'MM/dd/yy')}`,
              smsClientIds: smsClientList.map((client) => client.objectId),
              emailClientIds: emailClientList.map((client) => client.objectId),
              smsMessage: formData.smsMessage ? formData.smsMessage : undefined,
              emailSubject: formData.emailSubject
                ? formData.emailSubject
                : undefined,
              emailMessage: formData.emailMessage
                ? formData.emailMessage
                : undefined,
              smsTemplateId: template?.smsTemplate?.objectId,
              emailTemplateId: template?.emailTemplate?.objectId,
              collectionIdsSms:
                formData.collectionIds.length > 0
                  ? formData.collectionIds
                  : undefined,
              lookIdsSms:
                formData.lookIds.length > 0 ? formData.lookIds : undefined,
              productIdsSms:
                formData.productIds.length > 0
                  ? formData.productIds
                  : undefined,
              collectionIdsEmail:
                formData.collectionIds.length > 0
                  ? formData.collectionIds
                  : undefined,
              lookIdsEmail:
                formData.lookIds.length > 0 ? formData.lookIds : undefined,
              productIdsEmail:
                formData.productIds.length > 0
                  ? formData.productIds
                  : undefined,
              setAttachmentsSms: attachments,
              setAttachmentsEmail: attachments,
              callListId: callListId ?? undefined,
              callListDynamicId: callListDynamicId ?? undefined,
              fromUserRule: 'fromUser',
              fromUserId: user.objectId,
              fromName: null,
              fromEmail: null,
            }

            queueBroadcastMutation.mutate(broadcastData)
          },
        },
      ],
    })
  }
  function renderTextInsertButtons(
    key: 'smsMessage' | 'emailMessage',
    ref: React.RefObject<HTMLIonTextareaElement>
  ) {
    return (
      <MergeFieldButtons
        disabled={isEditingDisabled}
        mergeRef={ref}
        onInsert={(value) => setValue(key, value)}
      >
        <MergeFieldButton placeholder="<First Name>">
          First Name
        </MergeFieldButton>
        <MergeFieldButton placeholder="<Last Name>">Last Name</MergeFieldButton>
        <MergeFieldButton placeholder="<Lifetime Loyalty Points>">
          Lifetime Loyalty Points
        </MergeFieldButton>
        <MergeFieldButton placeholder="<Active Loyalty Points>">
          Active Loyalty Points
        </MergeFieldButton>
        <MergeFieldButton placeholder="<Active Loyalty Visits>">
          Active Loyalty Visits
        </MergeFieldButton>
        <MergeFieldButton placeholder="<Active Loyalty Rewards>">
          Active Loyalty Rewards
        </MergeFieldButton>
      </MergeFieldButtons>
    )
  }

  function renderPhoto() {
    return (
      <>
        {watchPhoto && (
          <div className="flex items-center justify-between">
            <IonImg
              src={typeof watchPhoto === 'string' ? watchPhoto : watchPhoto.pic}
              className="h-20"
            />
            <IonButton
              disabled={isEditingDisabled}
              fill="clear"
              size="small"
              onClick={() => {
                setValue('photo', null)
              }}
            >
              <IonIcon icon={close} />
            </IonButton>
          </div>
        )}
      </>
    )
  }

  function renderShopWithLink() {
    const link =
      watchCollectionIds.length > 0
        ? generateShopWithLinkPreview('collectionIds', watchCollectionIds)
        : watchLookIds.length > 0
        ? generateShopWithLinkPreview('lookIds', watchLookIds)
        : watchProductIds.length > 0
        ? generateShopWithLinkPreview('productIds', watchProductIds)
        : undefined

    return (
      <>
        {link ? (
          <div className="flex items-center gap-x-2">
            <IonRouterLink
              className="flex-1 overflow-hidden text-ellipsis whitespace-nowrap leading-none"
              href={link}
              target="_blank"
            >
              {link}
            </IonRouterLink>

            <IonButton
              disabled={isEditingDisabled}
              fill="clear"
              size="small"
              onClick={() => {
                resetField('collectionIds')
                resetField('lookIds')
                resetField('productIds')
              }}
            >
              <IonIcon icon={close} />
            </IonButton>
          </div>
        ) : null}
      </>
    )
  }

  // type workaround to prevent an admin broadcast unmounting and resetting state causing this redirect
  if (!broadcastState.initialized && broadcastState.type !== 'admin') {
    return <Redirect to="/broadcasts" />
  }

  return (
    <IonPage>
      {isEditingDisabled ? null : (
        <BroadcastFab
          onShopWithSelected={handleShopWithSelected}
          onSelectPhoto={handleSelectPhoto}
          onTakePhoto={handleTakePhoto}
        />
      )}
      <IonHeader>
        <Header backRef="/tabs/home" title="Craft Your Message" btn={null} />
      </IonHeader>
      <IonContent>
        <div className="ion-padding-top flex items-center justify-center gap-x-2">
          <IonRouterLink
            routerLink="/broadcasts/create/clients"
            color="secondary"
            className="text-xl font-semibold"
          >
            Total Subscribers: {clientList.length}
          </IonRouterLink>
          {!broadcastState.prefiltered ? (
            <FilterButton
              disabled={isEditingDisabled}
              onClick={() => setIsFilterOpen(true)}
            />
          ) : null}
        </div>

        <IonRow className="flex-col items-center space-y-3">
          <div className="text-center">
            <p className="text-ion-color-yellow">Credits Used: {creditsUsed}</p>
            <p className="text-ion-color-secondary">
              Credits Left After:{' '}
              {!smsCredits.data || pullClientListQuery.isInitialLoading
                ? '...'
                : `${(
                    smsCredits.data?.smsCreditsRemaining - creditsUsed
                  ).toLocaleString()}/${smsCredits.data?.smsCredits.toLocaleString()}`}
            </p>
          </div>
          <IonButton
            size="small"
            color="yellow"
            fill="solid"
            disabled={isBroadcastDisabled}
            onClick={handleSubmit(handleSendBroadcast)}
          >
            Send Broadcast
          </IonButton>
        </IonRow>

        <IonItem>
          <IonLabel>Title:</IonLabel>
          <Controller
            control={control}
            name="broadcastTitle"
            render={({ field: { value, onChange } }) => (
              <IonInput
                disabled={isEditingDisabled}
                value={value}
                onIonChange={onChange}
                autocapitalize="on"
              />
            )}
          />
        </IonItem>

        <IonItem>
          <IonLabel>Select a Template</IonLabel>
          <IonSelect
            disabled={isEditingDisabled}
            interfaceOptions={{ cssClass: 'dark-ionic-filter' }}
            onIonChange={handleTemplateSelect}
            okText={commonStrings.Okay}
            cancelText={commonStrings.Dismiss}
            className="bg-transparent"
          >
            {templatesQuery.data?.map((template, index) => (
              <IonSelectOption key={index} value={template.objectId}>
                {template.name}
              </IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>

        <div className="ml-5">
          <Controller
            name="includeRecentContacts"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Checkbox
                id="includeRecentContacts"
                checked={value}
                label="Include Recently Contacted"
                onChange={(b) => {
                  onChange(b)
                  setBroadcastFilter('includeRecentContacts', b)
                }}
              />
            )}
          />
        </div>

        <div className="ml-5">
          <Controller
            name="includeSms"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Checkbox
                id="includeSms"
                label="Include Text Message"
                checked={value}
                onChange={(e) => {
                  onChange(e)
                  setBroadcastFilter('includeSms', e)
                }}
              />
            )}
          />
        </div>

        <div className="ml-5">
          <Controller
            name="includeEmail"
            control={control}
            render={({ field: { value, onChange } }) => (
              <Checkbox
                id="includeEmail"
                label="Include Email"
                checked={value}
                onChange={(e) => {
                  onChange(e)
                  setBroadcastFilter('includeEmail', e)
                }}
              />
            )}
          />
        </div>

        <div className="ml-5">
          <div className="pl-5">
            <Controller
              name="emailOnly"
              control={control}
              render={({ field: { value, onChange } }) => (
                <Checkbox
                  id="emailOnly"
                  label="Email Only"
                  disabled={!includeEmail}
                  checked={value}
                  onChange={(e) => {
                    onChange(e)
                    setBroadcastFilter('emailOnly', e)
                    updateClientList(pullClientListQuery.data ?? [])
                  }}
                />
              )}
            />
          </div>
        </div>

        {smsClientList.length > 0 && (
          <>
            <IonRow className="ion-padding-horizontal ion-justify-content-between ion-align-items-center">
              <IonCol size="auto">
                <h5>Text Message:</h5>
              </IonCol>
              <IonCol size="auto">
                <IonButton
                  routerLink="/broadcasts/create/clients?list=sms"
                  size="small"
                  color="tertiary"
                >
                  {smsClientList.length} Clients
                </IonButton>
              </IonCol>
            </IonRow>

            <Card color="secondary">
              <CardContent>
                <div className="flex justify-end">
                  <IonButton
                    disabled={isEditingDisabled}
                    size="small"
                    fill="outline"
                    onClick={() => setValue('smsMessage', '')}
                  >
                    Clear
                  </IonButton>
                </div>
                <Controller
                  control={control}
                  name="smsMessage"
                  render={({ field: { value, onChange } }) => (
                    <TextArea
                      disabled={isEditingDisabled}
                      ref={smsMessageRef}
                      value={value}
                      onIonChange={onChange}
                      placeholder="Write your text here..."
                      className="min-h-[66px]"
                      autoGrow
                      autocapitalize="on"
                    />
                  )}
                />

                <IonRow className="ion-justify-content-between">
                  <IonCol size="auto" className="space-x-2">
                    {renderTextInsertButtons('smsMessage', smsMessageRef)}
                  </IonCol>
                  <IonCol size="auto" className="ion-align-self-end">
                    <p>
                      <IonText
                        color={
                          watchSmsMessage.length > maxCharacterCount
                            ? 'danger'
                            : 'dark'
                        }
                      >
                        {watchSmsMessage.length}
                      </IonText>
                      /{maxCharacterCount}
                    </p>
                  </IonCol>
                </IonRow>
                {renderShopWithLink()}
                {renderPhoto()}
              </CardContent>
            </Card>
          </>
        )}

        {emailClientList.length > 0 && (
          <>
            <IonRow className="ion-padding-horizontal ion-justify-content-between ion-align-items-center">
              <IonCol size="auto">
                <h5>Email:</h5>
              </IonCol>
              <IonCol size="auto">
                <IonButton
                  routerLink="/broadcasts/create/clients?list=email"
                  size="small"
                  color="tertiary"
                >
                  {emailClientList.length} Clients
                </IonButton>
              </IonCol>
            </IonRow>

            <Card color="secondary">
              <CardContent>
                <IonItem color="secondary" style={{ '--padding-start': 0 }}>
                  <IonLabel>Subject:</IonLabel>
                  <Controller
                    control={control}
                    name="emailSubject"
                    render={({ field: { value, onChange } }) => (
                      <IonInput
                        disabled={isEditingDisabled}
                        value={value}
                        onIonChange={onChange}
                        name="subject"
                        autocapitalize="on"
                      />
                    )}
                  />
                </IonItem>
                <div className="ion-padding-top">
                  <div className="flex justify-end">
                    <IonButton
                      disabled={isEditingDisabled}
                      size="small"
                      fill="outline"
                      onClick={() => setValue('emailMessage', '')}
                    >
                      Clear
                    </IonButton>
                  </div>

                  <Controller
                    control={control}
                    name="emailMessage"
                    render={({ field: { value, onChange } }) => (
                      <TextArea
                        disabled={isEditingDisabled}
                        ref={emailMessageRef}
                        value={value}
                        onIonChange={onChange}
                        placeholder="Write your email here..."
                        autoGrow
                        autocapitalize="on"
                      />
                    )}
                  />
                  <IonRow className="ion-justify-content-between">
                    <IonCol size="auto" className="space-x-2">
                      {renderTextInsertButtons('emailMessage', emailMessageRef)}
                    </IonCol>
                  </IonRow>
                  {renderShopWithLink()}
                  {renderPhoto()}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        <IonModal
          isOpen={isFilterOpen}
          onDidDismiss={() => setIsFilterOpen(false)}
        >
          <PullClientsModal
            filters={filters}
            onCancel={() => setIsFilterOpen(false)}
            onSearch={(filterData: FiltersFromForm) => {
              setFilters(filterData)
              setIsFilterOpen(false)
            }}
          />
        </IonModal>

        <IonLoading
          isOpen={pullClientListQuery.isInitialLoading}
          message="Loading subscribed clients..."
        />
      </IonContent>
    </IonPage>
  )
}

export default CreateBroadcast
